import * as React from "react"
import { cn } from "@/lib/utils"

const INTEGER_RE = /^[0-9]*$/
const DECIMAL_RE = /^[0-9]*\.?[0-9]*$/

interface NumericInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type'> {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  allowDecimals?: boolean
  /**
   * Receives the text as it is typed, also when it is empty or out of the
   * range and so is not a value. With it the field keeps what was typed when
   * it loses focus, instead of going back to the last valid value: whoever
   * listens decides what the text is worth.
   */
  onTextChange?: (text: string) => void
}

const NumericInput = React.forwardRef<HTMLInputElement, NumericInputProps>(
  ({ className, value, onChange, min, max, allowDecimals = false, onTextChange, ...props }, ref) => {
    const [display, setDisplay] = React.useState(String(value))

    React.useEffect(() => {
      setDisplay(String(value))
    }, [value])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      if (raw === '') {
        setDisplay('')
        onTextChange?.('')
        return
      }
      const pattern = allowDecimals ? DECIMAL_RE : INTEGER_RE
      if (!pattern.test(raw)) return
      setDisplay(raw)
      onTextChange?.(raw)
      const parsed = allowDecimals ? parseFloat(raw) : parseInt(raw, 10)
      if (isNaN(parsed)) return
      if (min !== undefined && parsed < min) return
      if (max !== undefined && parsed > max) return
      onChange(parsed)
    }

    const handleBlur = () => {
      if (onTextChange) return
      setDisplay(String(value))
    }

    return (
      <input
        ref={ref}
        type="text"
        inputMode={allowDecimals ? "decimal" : "numeric"}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        {...props}
      />
    )
  }
)
NumericInput.displayName = "NumericInput"

export { NumericInput }
